/**
 * Factory class for creating and managing ResizeObserver instances.
 */
export default class ResizeObserverFactory {
    private _maps: { [key: string]: ResizeObserver };

    constructor() {
        /** @type {Object.<string, ResizeObserver>} */
        this._maps = {};
    }

    /**
     * Connects elements to a resize observer.
     * @param {string} id - The unique identifier for the observer.
     * @param {any} dotNetRef - The .NET reference object.
     * @param {HTMLElement[]} elements - The elements to observe.
     * @param {string[]} elementIds - The IDs of the elements to observe.
     * @param {Object} options - The options for the observer.
     * @returns {DOMRect[]} - The initial sizes of the observed elements.
     */
    connect(id: string, dotNetRef: any, elements: HTMLElement[], elementIds: string[], options: Record<string, any>): DOMRect[] {
        let existingEntry = this._maps[id];
        if (!existingEntry) {
            let observer = new ResizeObserver(dotNetRef, options);
            this._maps[id] = observer;
        }

        let result = this._maps[id].connect(elements, elementIds);
        return result;
    }

    /**
     * Disconnects an element from the resize observer.
     * @param {string} id - The unique identifier for the observer.
     * @param {string} elementId - The ID of the element to disconnect.
     */
    disconnect(id: string, elementId: string): void {
        let existingEntry = this._maps[id];
        if (existingEntry) {
            existingEntry.disconnect(elementId);
        }
    }

    /**
     * Cancels the listener for the observer.
     * @param {string} id - The unique identifier for the observer.
     */
    cancelListener(id: string): void {
        let existingEntry = this._maps[id];
        if (existingEntry) {
            existingEntry.cancelListener();
            delete this._maps[id];
        }
    }
}

/**
 * Class for observing resize events on elements.
 */
class ResizeObserver {
    private logger: (message?: any, ...optionalParams: any[]) => void;
    private options: Record<string, any>;
    private _dotNetRef: any;
    private throttleResizeHandlerId: number;
    private _observervedElements: Array<{ element: HTMLElement, id: string, isInitialized: boolean }>;
    private _resizeObserver: globalThis.ResizeObserver;

    /**
     * @param {any} dotNetRef - The .NET reference object.
     * @param {Object} options - The options for the observer.
     */
    constructor(dotNetRef: any, options: Record<string, any>) {
        this.logger = options.enableLogging ? console.log : (message) => { };
        this.options = options;
        this._dotNetRef = dotNetRef;

        let delay = (this.options || {}).reportRate || 200;

        this.throttleResizeHandlerId = -1;

        /** @type {Array<{element: HTMLElement, id: string, isInitialized: boolean}>} */
        this._observervedElements = [];

        this.logger('[FeBlazorLibrary | ResizeObserver] Observer initialized');

        this._resizeObserver = new globalThis.ResizeObserver(entries => {
            let changes: Array<{ id: string, size: DOMRect }> = [];
            this.logger('[FeBlazorLibrary | ResizeObserver] changes detected');
            for (let entry of entries) {
                let target = entry.target as HTMLElement;
                let affectedObservedElement = this._observervedElements.find((x) => x.element == target);
                if (affectedObservedElement) {
                    let size = entry.target.getBoundingClientRect();
                    if (affectedObservedElement.isInitialized) {
                        changes.push({ id: affectedObservedElement.id, size: size });
                    } else {
                        affectedObservedElement.isInitialized = true;
                    }
                }
            }

            if (changes.length > 0) {
                if (this.throttleResizeHandlerId >= 0) {
                    clearTimeout(this.throttleResizeHandlerId);
                }

                this.throttleResizeHandlerId = window.setTimeout(this.resizeHandler.bind(this, changes), delay);
            }
        });
    }

    /**
     * Handles resize events and notifies the .NET reference.
     * @param {Array<{id: string, size: DOMRect}>} changes - The changes in size.
     */
    resizeHandler(changes: Array<{ id: string, size: DOMRect }>): void {
        try {
            this.logger("[FeBlazorLibrary | ResizeObserver] OnSizeChanged handler invoked");
            this._dotNetRef.invokeMethodAsync("OnSizeChanged", changes);
        } catch (error) {
            this.logger("[FeBlazorLibrary | ResizeObserver] Error in OnSizeChanged handler:", { error });
        }
    }

    /**
     * Connects elements to the resize observer.
     * @param {HTMLElement[]} elements - The elements to observe.
     * @param {string[]} ids - The IDs of the elements to observe.
     * @returns {DOMRect[]} - The initial sizes of the observed elements.
     */
    connect(elements: HTMLElement[], ids: string[]): DOMRect[] {
        let result: DOMRect[] = [];
        this.logger('[FeBlazorLibrary | ResizeObserver] Start observing elements...');

        for (let i = 0; i < elements.length; i++) {
            let newEntry = {
                element: elements[i],
                id: ids[i],
                isInitialized: false,
            };

            this.logger("[FeBlazorLibrary | ResizeObserver] Start observing element:", { newEntry });

            result.push(elements[i].getBoundingClientRect());

            this._observervedElements.push(newEntry);
            this._resizeObserver.observe(elements[i]);
        }

        return result;
    }

    /**
     * Disconnects an element from the resize observer.
     * @param {string} elementId - The ID of the element to disconnect.
     */
    disconnect(elementId: string): void {
        this.logger('[FeBlazorLibrary | ResizeObserver] Try to unobserve element with id', { elementId });

        let affectedObservedElement = this._observervedElements.find((x) => x.id == elementId);
        if (affectedObservedElement) {
            let element = affectedObservedElement.element;
            this._resizeObserver.unobserve(element);
            this.logger('[FeBlazorLibrary | ResizeObserver] Element found. Unobserving size changes of element', { element });

            let index = this._observervedElements.indexOf(affectedObservedElement);
            this._observervedElements.splice(index, 1);
        }
    }

    /**
     * Cancels the listener and disconnects all observed elements.
     */
    cancelListener(): void {
        this.logger('[FeBlazorLibrary | ResizeObserver] Closing ResizeObserver. Detaching all observed elements');

        this._resizeObserver.disconnect();
        this._dotNetRef = undefined;
    }
}
